/**
 * "Show call pops in this browser" (Zac, 2026-11-02): per user AND per
 * device, so a front desk that runs the Velora desktop app (Windows call
 * notifications and its own pop window) does not get a second pop in the
 * browser, while the same person keeps browser pops on another PC.
 *
 * Stored in localStorage under the user's id, only when the person chooses
 * (`on` / `off`: the card's menu, Settings, the Phones settings drawer,
 * Ctrl+K). With no choice, browser pops are on unless the desktop app pops
 * calls on this PC (`desktopPopsOn`): inside the app's own window, or when
 * the app's loopback status says `callPops: "on"` (0.4.4+). An older app,
 * an app with its pops off or signed out, or no app: browser pops stay on.
 *
 * Off stops only the browser's pop panel (the web app sends no browser
 * notifications for calls). The Phones page still lists live calls.
 */
import { toast, type MenuEntry } from '@velora/ui';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useDesktopApp, type DesktopAppState } from '../../../app/desktop/presence.js';
import { useSession } from '../../../app/shell.js';

export type StoredBrowserPops = 'on' | 'off';

const KEY = (userId: string) => `velora.callpop.browser.${userId}`;
const HINT_KEY = (userId: string) => `velora.callpop.browser-hint.${userId}`;
export const MINIMIZED_KEY = 'velora.callpop.minimized';

export const BROWSER_POPS_LABEL = 'Show call pops in this browser';
export const NOTIFICATION_SETTINGS_PATH = '/settings/notifications';

/** The hint shown once when the desktop app takes over call pops. */
export const DESKTOP_HINT =
  'Call pops show in the Velora desktop app. Turn browser pops back on in Settings.';

const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}
function subscribe(l: () => void): () => void {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key.startsWith('velora.callpop.')) l();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private window: the choice lasts for this page only */
  }
  emit();
}

export function readStoredBrowserPops(userId: string): StoredBrowserPops | null {
  const v = read(KEY(userId));
  return v === 'on' || v === 'off' ? v : null;
}

/** The person's own choice (always wins over the desktop-app default). */
export function setBrowserPops(userId: string, on: boolean): void {
  write(KEY(userId), on ? 'on' : 'off');
}

/**
 * Does the Velora desktop app pop calls on this PC? `null` while the probe
 * is still answering.
 */
export function desktopPopsOn(desktop: DesktopAppState): boolean | null {
  switch (desktop.state) {
    case 'checking':
      return null;
    case 'in-app':
      return true;
    case 'installed':
      return desktop.callPops === 'on';
    default:
      return false;
  }
}

/**
 * Are browser pops on? The person's choice, else off exactly when the
 * desktop app pops calls here. `null` while that is unknown (the probe
 * answers within ~1 s), so a pop never flashes on a desktop-app PC.
 */
export function browserPopsEnabled(
  stored: StoredBrowserPops | null,
  desktop: DesktopAppState,
): boolean | null {
  if (stored !== null) return stored === 'on';
  const app = desktopPopsOn(desktop);
  return app === null ? null : !app;
}

export interface BrowserPops {
  /** null while unknown (see browserPopsEnabled). */
  enabled: boolean | null;
  /** True when the setting is the default because the desktop app pops calls. */
  byDesktopApp: boolean;
  set: (on: boolean) => void;
}

/** The setting for this user on this device. `desktop` is useDesktopApp(). */
export function useBrowserPops(userId: string, desktop: DesktopAppState): BrowserPops {
  const stored = useSyncExternalStore(
    subscribe,
    () => readStoredBrowserPops(userId),
    () => null,
  );
  const set = useCallback((on: boolean) => setBrowserPops(userId, on), [userId]);
  return {
    enabled: browserPopsEnabled(stored, desktop),
    byDesktopApp: stored === null && desktopPopsOn(desktop) === true,
    set,
  };
}

/**
 * The first time the desktop app's pops turn browser pops off by default
 * (no choice made), show the hint once per user per device.
 */
export function useDesktopHint(userId: string, pops: BrowserPops, show: (hint: string) => void) {
  const due = pops.byDesktopApp;
  useEffect(() => {
    if (!due || read(HINT_KEY(userId)) !== null) return;
    write(HINT_KEY(userId), '1');
    show(DESKTOP_HINT);
    // `show` is a fresh closure each render; the effect runs when it becomes due.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, due]);
}

/* ---------- minimised state (per device) ---------- */

export function readMinimized(): boolean {
  return read(MINIMIZED_KEY) === '1';
}

export function useMinimized(): readonly [boolean, (m: boolean) => void] {
  const m = useSyncExternalStore(subscribe, readMinimized, () => false);
  const set = useCallback((next: boolean) => write(MINIMIZED_KEY, next ? '1' : '0'), []);
  return [m, set] as const;
}

/* ---------- shared by the global host and the Phones page's own pop ---------- */

/** The setting for the signed-in person on this device. */
export function useMyBrowserPops(): BrowserPops & { userId: string } {
  const userId = useSession().user.id;
  return { ...useBrowserPops(userId, useDesktopApp()), userId };
}

/** Turned off: a toast that says where to turn it back on. */
export function browserPopsOffToast(): void {
  toast({
    id: 'callpop-browser',
    title: 'Call pops are off in this browser',
    description: 'Turn them back on in Settings > Notifications, or with Ctrl+K.',
  });
}

/**
 * The browser pop's remembered minimise and its menu entry ("Show call pops
 * in this browser", checked; unticking turns them off on this device).
 */
export function useBrowserPopProps(pops: Pick<BrowserPops, 'set'>): {
  minimized: readonly [boolean, (m: boolean) => void];
  menu: MenuEntry[];
} {
  const minimized = useMinimized();
  const { set } = pops;
  const menu = useMemo<MenuEntry[]>(
    () => [
      {
        type: 'checkbox',
        id: 'browser-pops',
        label: BROWSER_POPS_LABEL,
        checked: true,
        onCheckedChange: (on) => {
          if (on) return;
          set(false);
          browserPopsOffToast();
        },
      },
    ],
    [set],
  );
  return { minimized, menu };
}
